import { useEffect, useMemo, useState } from "react";

function prettySize(bytes) {
  if (bytes === 0) return "0 o";
  if (!bytes && bytes !== 0) return "";
  const units = ["o", "Ko", "Mo", "Go"];
  let i = 0;
  let v = bytes;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
}

function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve({ img, url });
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}

function canvasToBlob(canvas, mimeType, quality) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob || null), mimeType, quality);
  });
}

export default function ImageCompressor() {
  const [file, setFile] = useState(null);

  // Réglages
  const [format, setFormat] = useState("image/jpeg"); // "image/jpeg" | "image/webp"
  const [quality, setQuality] = useState(0.82); // 0.4 -> 1
  const [maxWidth, setMaxWidth] = useState(1920); // px

  // Résultat
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null); // { url, name, size }

  // Clean URL quand on change de résultat / unmount
  useEffect(() => {
    return () => {
      if (result?.url) URL.revokeObjectURL(result.url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const beforeSize = file ? file.size : null;

  const savings = useMemo(() => {
    if (!beforeSize || !result?.size) return null;
    const diff = beforeSize - result.size;
    const pct = (diff / beforeSize) * 100;
    return { diff, pct };
  }, [beforeSize, result]);

  const resetResult = () => {
    setError("");
    if (result?.url) URL.revokeObjectURL(result.url);
    setResult(null);
  };

  const onPickFile = (f) => {
    resetResult();
    setFile(f || null);
  };

  const compress = async () => {
    if (!file) return;

    setLoading(true);
    setError("");
    resetResult();

    try {
      const { img, url: inputUrl } = await loadImageFromFile(file);

      // Resize (contain)
      const scale = Math.min(1, maxWidth / img.width);
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));

      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        URL.revokeObjectURL(inputUrl);
        throw new Error("Canvas non supporté par ton navigateur.");
      }

      ctx.drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(inputUrl);

      // toBlob peut renvoyer null si format non supporté
      let blob = await canvasToBlob(canvas, format, quality);

      // Fallback automatique : si WebP non supporté => JPEG
      if (!blob && format === "image/webp") {
        blob = await canvasToBlob(canvas, "image/jpeg", quality);
      }

      if (!blob) {
        throw new Error(
          "Impossible de générer l’image compressée (format non supporté)."
        );
      }

      const outExt = blob.type === "image/webp" ? "webp" : "jpg";
      const baseName = file.name.replace(/\.\w+$/, "");
      const outName = `${baseName}-compressed.${outExt}`;

      const outUrl = URL.createObjectURL(blob);
      setResult({ url: outUrl, name: outName, size: blob.size });
    } catch (e) {
      setError(
        e && e.message
          ? e.message
          : "Une erreur est survenue pendant la compression."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section style={{ padding: 16 }}>
      <h2>Réduire la taille d’une image</h2>
      <small>Traitement local : rien n’est envoyé sur Internet</small>

      <input
        type="file"
        accept="image/*"
        onChange={(e) => onPickFile(e.target.files && e.target.files[0])}
        disabled={loading}
      />

      <div style={{ maxWidth: 420, margin: "12px auto", textAlign: "left" }}>
        <label style={{ display: "block", marginBottom: 10 }}>
          Format de sortie
          <select
            value={format}
            onChange={(e) => setFormat(e.target.value)}
            disabled={loading}
            style={{
              width: "100%",
              padding: 10,
              marginTop: 6,
              borderRadius: 5,
              border: "1px solid #ccc",
            }}
          >
            <option value="image/jpeg">JPEG (compatible partout)</option>
            <option value="image/webp">WebP (souvent plus léger)</option>
          </select>
        </label>

        <label style={{ display: "block", marginBottom: 10 }}>
          Largeur max (px)
          <input
            type="number"
            min={320}
            max={8000}
            value={maxWidth}
            onChange={(e) => setMaxWidth(Number(e.target.value))}
            disabled={loading}
            style={{
              width: "100%",
              padding: 10,
              marginTop: 6,
              borderRadius: 5,
              border: "1px solid #ccc",
            }}
          />
        </label>

        <label style={{ display: "block", marginBottom: 6 }}>
          Qualité : {Math.round(quality * 100)}%
          <input
            type="range"
            min="0.4"
            max="1"
            step="0.02"
            value={quality}
            onChange={(e) => setQuality(Number(e.target.value))}
            disabled={loading}
            style={{ width: "100%" }}
          />
        </label>
      </div>

      <button onClick={compress} disabled={!file || loading}>
        {loading ? "Compression..." : "Compresser"}
      </button>

      <div className="output" style={{ marginTop: 12 }}>
        {file && (
          <p>
            Avant : <b>{prettySize(file.size)}</b>
          </p>
        )}

        {error && (
          <p style={{ color: "#b00020" }}>
            <b>Erreur :</b> {error}
          </p>
        )}

        {result && (
          <>
            <p>
              Après : <b>{prettySize(result.size)}</b>{" "}
              {savings && (
                <span>
                  (gain : <b>{prettySize(savings.diff)}</b> —{" "}
                  <b>{savings.pct.toFixed(1)}%</b>)
                </span>
              )}
            </p>

            <a href={result.url} download={result.name}>
              Télécharger l’image optimisée
            </a>
          </>
        )}
      </div>
    </section>
  );
}
